/**
 * RenameInput Component
 * Floating input for renaming dashboard tabs
 * Migrated from legacy RenameInput.js
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { RenameInputProps } from './types';
import './RenameInput.css';

const RenameInput: React.FC<RenameInputProps> = ({
  inputText: initialText,
  dashboard,
  saveRenameDashboard,
  closeRename,
}) =>
{
  const [inputText, setInputText] = useState(initialText);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() =>
  {
    // Double rAF to ensure dnd-kit transforms are fully applied
    requestAnimationFrame(() =>
    {
      requestAnimationFrame(() =>
      {
        const parentTab = containerRef.current?.closest('.tab-item') || containerRef.current;
        if (parentTab)
        {
          const rect = parentTab.getBoundingClientRect();
          const inputHeight = 26; // input height + margins
          const inputWidth = 200; // input + save button approximate width
          setPosition({
            top: rect.top + (rect.height - inputHeight) / 2,
            left: rect.left + (rect.width - inputWidth) / 2,
          });
        }

        // Focus input
        setTimeout(() =>
        {
          if (inputRef.current)
          {
            inputRef.current.focus();
            inputRef.current.select();
          }
        }, 0);
      });
    });
  }, []);

  const handleChangeText = useCallback((event: React.ChangeEvent<HTMLInputElement>) =>
  {
    setInputText(event.target.value);
  }, []);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) =>
    {
      event.stopPropagation(); // prevent dnd-kit on the parent <li> from intercepting Space/Enter
      if (event.key === 'Enter' || event.keyCode === 13)
      {
        handleSave(event);
      }
      if (event.key === 'Escape' || event.keyCode === 27)
      {
        closeRename();
      }
    },
    [inputText, dashboard, closeRename],
  );

  const handleSave = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent) =>
    {
      if (inputText.trim() === '')
      {
        return;
      }
      saveRenameDashboard(event, dashboard.DashboardId, inputText);
    },
    [inputText, dashboard, saveRenameDashboard],
  );

  // The rename input is portaled to document.body, but React events still bubble
  // through the React tree to the parent <li>, which carries dnd-kit's listeners.
  // Without this, dragging to select text starts a tab drag (PointerSensor distance: 8).
  const stopDragActivation = useCallback(
    (event: React.PointerEvent | React.MouseEvent) =>
    {
      event.stopPropagation();
    },
    [],
  );

  const handleBackdropClick = useCallback(() =>
  {
    closeRename();
  }, [closeRename]);

  return (
    <div className="editable-container-tabs" ref={containerRef}>
      {createPortal(
        <div
          className="container-tabs-floating"
          onPointerDown={stopDragActivation}
          onMouseDown={stopDragActivation}
          onClick={stopDragActivation}
          onDoubleClick={stopDragActivation}
          style={{
            left: position?.left ?? -9999,
            top: position?.top ?? -9999,
            visibility: position ? 'visible' : 'hidden',
          }}
        >
          <input
            ref={inputRef}
            id="input-rename-tabs-o"
            name="message"
            type="text"
            className="corgee-input-rename-tabs"
            value={inputText}
            onKeyDown={handleKeyPress}
            onChange={handleChangeText}
          />
          <button type="button" className="corgee-btn-save-tabs" onClick={handleSave}>
            Save
          </button>
        </div>,
        document.body,
      )}

      {createPortal(
        <div className="corgee_com_card-action-menu-backdrop-tabs" onClick={handleBackdropClick} />,
        document.body,
      )}
    </div>
  );
};

export default RenameInput;
